import { usePaneLocation } from "@/hooks/pane-routing";
import { markViewedItemsAsRead } from "@/utils/inboxItemReadStatus";
import {
  InboxItem,
  InboxItemsGetParams,
  UpdateInboxItemRequest,
} from "@cohesive/api-client";
import {
  useInfiniteQuery,
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useEffect, useRef } from "react";
import { orgAPI } from "./api";

export function useInboxItems<T = InboxItem>(
  options?: Partial<UseQueryOptions<InboxItem[], AxiosError, T[]>>,
  query?: InboxItemsGetParams,
) {
  const rightPaneLocation = usePaneLocation("rightPane");
  const idsToUpdateRef = useRef<string[]>([]);
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: ["inboxItems", query],
    queryFn: async () => {
      const response = await orgAPI.inbox.list({
        params: query,
      });
      const filtered = response.data.inbox_items.filter(
        (item) => item.flow_session?.status !== "initialized",
      );

      const { items, idsToUpdate } = markViewedItemsAsRead(
        filtered,
        rightPaneLocation,
      );
      idsToUpdateRef.current = idsToUpdate;

      return items;
    },
    ...options,
  });

  // Trigger server updates when data is successfully fetched
  useEffect(() => {
    if (result.isSuccess && idsToUpdateRef.current.length > 0) {
      const idsToUpdate = [...idsToUpdateRef.current];
      idsToUpdateRef.current = [];

      // Trigger server updates for viewed items
      idsToUpdate.forEach(async (id) => {
        try {
          await orgAPI.inbox.update(id, { is_read: true });
        } catch (error) {
          console.error(
            `Failed to update read status for inbox item ${id}`,
            error,
          );
        }
      });
    }
  }, [result.isSuccess, result.dataUpdatedAt, queryClient]);

  return result;
}

export function useInboxItemsInfinite(filters: InboxItemsGetParams) {
  const rightPaneLocation = usePaneLocation("rightPane");
  const idsToUpdateRef = useRef<string[]>([]);
  const queryClient = useQueryClient();

  const result = useInfiniteQuery({
    queryKey: ["inboxItemsInfinite", filters],
    queryFn: async ({ pageParam }: { pageParam?: string }) => {
      const params: InboxItemsGetParams = { ...filters };

      // Add pagination param
      if (pageParam) {
        params.before_ts = pageParam;
      }

      const response = await orgAPI.inbox.list({ params });
      const filtered = response.data.inbox_items.filter(
        (item) => item.flow_session?.status !== "initialized",
      );

      const { items, idsToUpdate } = markViewedItemsAsRead(
        filtered,
        rightPaneLocation,
      );

      // Accumulate IDs across pages
      idsToUpdateRef.current = [...idsToUpdateRef.current, ...idsToUpdate];

      return items;
    },
    getNextPageParam: (lastPage: InboxItem[]) => {
      if (lastPage.length === 0) return undefined;
      // Use the created_at timestamp of the last item as the next page param
      const lastItem = lastPage[lastPage.length - 1];
      return lastItem.created_at;
    },
    initialPageParam: undefined,
  });

  // Trigger server updates when data is successfully fetched
  useEffect(() => {
    if (result.isSuccess && idsToUpdateRef.current.length > 0) {
      const idsToUpdate = [...idsToUpdateRef.current];
      idsToUpdateRef.current = [];

      // Trigger server updates for viewed items
      idsToUpdate.forEach(async (id) => {
        try {
          await orgAPI.inbox.update(id, { is_read: true });
        } catch (error) {
          console.error(
            `Failed to update read status for inbox item ${id}`,
            error,
          );
        }
      });
    }
  }, [result.isSuccess, result.dataUpdatedAt, queryClient]);

  return result;
}

export function useInboxItemById(
  itemId: string,
  options?: Partial<UseQueryOptions<InboxItem, AxiosError>>,
) {
  const rightPaneLocation = usePaneLocation("rightPane");
  const shouldUpdateRef = useRef<boolean>(false);
  const queryClient = useQueryClient();

  const result = useQuery({
    queryKey: ["inboxItem", itemId],
    queryFn: async () => {
      const response = await orgAPI.inbox.getById(itemId);
      const item = response.data.inbox_item;

      const { items, idsToUpdate } = markViewedItemsAsRead(
        [item],
        rightPaneLocation,
      );

      shouldUpdateRef.current = idsToUpdate.length > 0;
      return items[0];
    },
    enabled: !!itemId,
    ...options,
  });

  // Trigger server update when data is successfully fetched
  useEffect(() => {
    if (result.isSuccess && shouldUpdateRef.current && itemId) {
      shouldUpdateRef.current = false;

      orgAPI.inbox.update(itemId, { is_read: true }).catch((error) => {
        console.error(
          `Failed to update read status for inbox item ${itemId}`,
          error,
        );
      });
    }
  }, [result.isSuccess, result.dataUpdatedAt, itemId, queryClient]);

  return result;
}

export function useUpdateInboxItem(
  options?: UseMutationOptions<
    InboxItem,
    AxiosError,
    { id: string; data: UpdateInboxItemRequest }
  >,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["updateInboxItem"],
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: UpdateInboxItemRequest;
    }) => {
      const response = await orgAPI.inbox.update(id, data);
      return response.data.inbox_item;
    },
    onSuccess: (updatedItem, { id }) => {
      // Update specific item in cache
      queryClient.setQueryData(["inboxItem", id], updatedItem);

      // Invalidate general inbox items queries
      queryClient.invalidateQueries({ queryKey: ["inboxItems"] });

      // Invalidate general infinite queries to handle filtering properly
      queryClient.invalidateQueries({ queryKey: ["inboxItemsInfinite"] });

      //Update regular inbox items queries
      queryClient.setQueriesData(
        { queryKey: ["inboxItems"] },
        (oldData: InboxItem[] | undefined) => {
          if (!oldData) return oldData;
          return oldData.map((item) =>
            item.id === id
              ? {
                  ...item, // Preserve all other item properties because we get back less data on read item updates
                  is_read: updatedItem.is_read,
                  is_starred: updatedItem.is_starred,
                  is_archived: updatedItem.is_archived,
                }
              : item,
          );
        },
      );
    },
    ...options,
  });
}

export function useEventStreamUrl(
  options?: Partial<UseQueryOptions<string, AxiosError>>,
) {
  return useQuery({
    queryKey: ["eventStreamUrl"],
    queryFn: async () => {
      return await orgAPI.eventStream.getEventStreamUrl();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - path is stable; auth is per connect
    ...options,
  });
}
